import React, { useState, useEffect } from 'react'
import api, { IConversationListItem } from 'helpers/api'
import Spinner from 'components/spinner'

interface ConversationHistoryProps {
  onSelect: (conversation: IConversationListItem) => void
  onClose: () => void
}

function timeAgo(dateString: string): string {
  // The API sends naive UTC timestamps; without 'Z' the browser reads them as local time.
  const seconds = Math.floor((Date.now() - new Date(dateString + 'Z').getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

const ConversationHistory: React.FC<ConversationHistoryProps> = ({ onSelect, onClose }) => {
  const [conversations, setConversations] = useState<IConversationListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)

  const PER_PAGE = 20

  const fetchConversations = async (pageNum: number, append: boolean) => {
    try {
      const response = await api.getConversations(pageNum, PER_PAGE)
      const items = response.data
      setConversations((prev) => (append ? [...prev, ...items] : items))
      setHasMore(items.length === PER_PAGE)
      setLoading(false)
      setLoadingMore(false)
    } catch {
      // silently fail — user sees empty state
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    fetchConversations(1, false)
  }, [])

  const loadMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    setLoadingMore(true)
    fetchConversations(nextPage, true)
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface-muted">
        <Spinner size="small" />
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-surface-muted text-muted text-sm">
        <span>No conversations yet</span>
        <button className="mt-3 text-xs text-blue cursor-pointer bg-transparent border-0 underline" onClick={onClose}>
          Start a new chat
        </button>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto bg-surface-muted">
      <div className="px-3 pt-3 pb-1">
        <div className="text-xs text-muted font-medium mb-2 px-1">Recent conversations</div>
        {conversations.map((conv) => (
          <button
            key={conv.publicId}
            className="w-full text-left bg-surface rounded-xl px-3.5 py-3 mb-2 border border-divider cursor-pointer transition-[border-color,box-shadow] duration-150 hover:border-blue hover:shadow-[0_0_0_3px_rgba(46,127,249,0.08)]"
            onClick={() => onSelect(conv)}
          >
            <div className="text-sm text-body leading-[1.4] line-clamp-2">{conv.preview || 'Empty conversation'}</div>
            <div className="flex items-center gap-2 mt-1.5 text-xs text-muted">
              <span>{conv.messageCount} messages</span>
              <span>·</span>
              <span>{timeAgo(conv.updatedAt)}</span>
            </div>
          </button>
        ))}
        {hasMore && (
          <button
            className="w-full text-center text-xs text-blue cursor-pointer bg-transparent border-0 py-2 hover:underline"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  )
}

export default ConversationHistory
